import React from "react"
import { RecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ModalityGraphGroupConfig } from "../../../../Types/ModalityGraphGroup"
import { getVisibleGraphs } from "../visibleGraphs"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { useMobergMenu } from "../../../../../../../../Hooks/useMobergMenu"
import { useState } from "react"
import { TraceDataConfig } from "../../../../Types/Trace"
import { MobergContextMenuItem } from "../../../../../../../../Components/MobergMenu/MobergContextMenuItem"
import { MdTune, MdVisibility, MdVisibilityOff } from "react-icons/md"
import { MobergIconSize } from "../../../../../../../../Moberg"
import { MobergMenuDivider } from "../../../../../../../../Components/MobergMenu/MobergMenuDivider"
import { goalBarOwner } from "../../../../Types/GoalRanges"

type TraceVisibilityContextMenuOptions = {
    // Right click a graph's label, "Edit graph settings": opens the window's configuration at that graph.
    onEditGraph?: (graphId: string) => void
}

export const useTraceVisibilityContextMenu = (atom: RecoilState<ModalityGraphGroupConfig>, options?: TraceVisibilityContextMenuOptions) => {
    const setConfig = useSetRecoilState(atom)
    const { patientModalities } = useRecoilValue(currentPatientFileInfoAtom)
    const [traceVisibilityMenuData, setTraceVisibilityMenuData] = useState<{graphId: string, traceConfig: TraceDataConfig}>()
    const menu = useMobergMenu({ placement: "right-start", isContextMenu: true })

    const setTraceVisibility = (getVisibility: (graphId: string, traceId: string, value: boolean) => boolean, withBars = false) => {
        setConfig(previous => {
            const newGraphs = previous.graphs.map(graph => ({
                ...graph,
                traces: graph.traces.map((trace: any) => {
                    const isVisible = getVisibility(graph.id, trace.id, trace.isVisible ?? true)
                    return withBars && trace.goal && graph.id === traceVisibilityMenuData?.graphId ?{ ...trace, isVisible, goal: { ...trace.goal, barHidden: !isVisible } } : { ...trace, isVisible }
                })
            }))

            // If there are no visible graphs, make sure "hideEmptyGraphs" is false, otherwise, no change
            const noVisibleGraphs = getVisibleGraphs(newGraphs, patientModalities, true).length === 0
            return {
                ...previous, 
                graphs: newGraphs, 
                hideEmptyGraphs: noVisibleGraphs ? false : previous.hideEmptyGraphs 
            }
        })
        menu.close()
    }

    const toggleTraceVisibility = (graphId: string, traceId: string) => {
		// A goal bar's label shows or hides that bar alone.
		const owner = goalBarOwner(traceId)
		if (owner) {
			setConfig(previous => ({
				...previous,
				graphs: previous.graphs.map(graph => graph.id !== graphId ? graph : {
					...graph,
					traces: graph.traces.map((trace: any) => trace.id !== owner || !trace.goal ? trace : { ...trace, goal: { ...trace.goal, barHidden: !trace.goal.barHidden } })
				})
			}))
			return
		}
		setTraceVisibility((_graphId, _traceId, isVisible) => graphId === _graphId && traceId === _traceId ? !isVisible : isVisible)
	}

    const openTraceVisibilityContextMenu = (event: React.MouseEvent, traceConfig: TraceDataConfig, graphId: string) => {
        // A goal bar's menu acts on its measurement.
        const owner = goalBarOwner(traceConfig.id)
        setTraceVisibilityMenuData({ graphId, traceConfig: owner ? { ...traceConfig, id: owner } : traceConfig })
        menu.open(event)
    }

    const editGraph = () => {
        const graphId = traceVisibilityMenuData?.graphId
        menu.close()
        if (graphId && options?.onEditGraph) {
            options.onEditGraph(graphId)
        }
    }

    const TraceVisibilityContextMenu = (
        <menu.MobergMenu>
            {options?.onEditGraph && (<>
                <MobergContextMenuItem onClick={editGraph} icon={<MdTune size={MobergIconSize.SMALL} />}>
                    Edit graph settings
                </MobergContextMenuItem>

                <MobergMenuDivider />
            </>)}

            <MobergContextMenuItem onClick={() => setTraceVisibility((graphId, traceId, currentVisibility) => (
                graphId === traceVisibilityMenuData?.graphId && traceId === traceVisibilityMenuData?.traceConfig.id 
                    ? true 
                    : graphId === traceVisibilityMenuData?.graphId 
                        ? false 
                        : currentVisibility
                ), true)}
                icon={<MdVisibility size={MobergIconSize.SMALL} />}>
                Only show {traceVisibilityMenuData?.traceConfig?.name}
            </MobergContextMenuItem>

            <MobergMenuDivider />

            <MobergContextMenuItem onClick={() => setTraceVisibility((graphId, traceId, currentVisibility) => graphId === traceVisibilityMenuData?.graphId ? true : currentVisibility, true)}
                icon={<MdVisibility size={MobergIconSize.SMALL} />}>
                Show all
            </MobergContextMenuItem>
            <MobergContextMenuItem onClick={() => setTraceVisibility((graphId, traceId, currentVisibility) => graphId === traceVisibilityMenuData?.graphId ? false : currentVisibility, true)}
                icon={<MdVisibilityOff size={MobergIconSize.SMALL} />}>
                Hide all
            </MobergContextMenuItem>
        </menu.MobergMenu>
    )

    return { TraceVisibilityContextMenu, openTraceVisibilityContextMenu, setTraceVisibility, toggleTraceVisibility }
}